import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  History as HistoryIcon,
  CalendarDays,
  Filter,
  ChevronRight,
  FileText
} from 'lucide-react';

export default function History() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('all');

  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API}/groups`, { credentials: 'include' });
      if (response.ok) {
        setGroups(await response.json());
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedDate) {
        params.append('date', format(selectedDate, 'yyyy-MM-dd'));
      }
      if (selectedGroup && selectedGroup !== 'all') {
        params.append('group_id', selectedGroup);
      }

      const response = await fetch(`${API}/history?${params.toString()}`, {
        credentials: 'include'
      });
      if (response.ok) {
        setHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedGroup]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const clearFilters = () => {
    setSelectedDate(null);
    setSelectedGroup('all');
  };

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 flex items-center gap-3">
              <HistoryIcon className="w-8 h-8 text-violet-600" />
              Histórico
            </h1>
            <p className="text-zinc-500 mt-1">
              Consulte resumos anteriores
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Filter className="w-4 h-4" />
                Filtros:
              </div>

              {/* Date Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="justify-start text-left font-normal">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {selectedDate ? (
                      format(selectedDate, "dd 'de' MMMM, yyyy", { locale: ptBR })
                    ) : (
                      'Selecionar data'
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={ptBR}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Group Filter */}
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Todos os grupos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os grupos</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group.group_id} value={group.group_id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(selectedDate || selectedGroup !== 'all') && (
                <Button variant="ghost" onClick={clearFilters} className="text-zinc-500">
                  Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* History Table */}
        <Card className="border-zinc-200">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-600" />
              Resumos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-zinc-500 mt-4">Carregando...</p>
              </div>
            ) : history.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">Data</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead className="hidden md:table-cell">Resumo</TableHead>
                    <TableHead className="w-[100px]">Itens</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item, index) => (
                    <TableRow 
                      key={item.summary_id || index}
                      className="cursor-pointer hover:bg-zinc-50"
                      onClick={() => navigate(`/app/groups/${item.group_id}`)}
                      data-testid={`history-row-${index}`}
                    >
                      <TableCell className="font-mono text-sm">
                        {item.date}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-zinc-100 border-0">
                          {item.group_name}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <p className="text-sm text-zinc-600 line-clamp-1 max-w-md">
                          {item.one_liner}
                        </p>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-500">
                          {item.questions?.length || 0} pendentes
                        </span>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-zinc-400" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <HistoryIcon className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
                <p className="text-zinc-500">Nenhum histórico encontrado</p>
                <p className="text-sm text-zinc-400 mt-1">
                  {selectedDate || selectedGroup !== 'all' 
                    ? 'Tente ajustar os filtros' 
                    : 'Gere resumos nos grupos para criar histórico'
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
